// The app has no server, so the only place a crash can be reported to is the browser it happened in. Every
// uncaught error (render, event handler, promise) is appended here; the error page and Settings show the list
// with a copy button so it can be pasted into an issue.
export interface CrashEntry {
  at: number;
  url: string;
  message: string;
  stack?: string;
  kind: 'render' | 'error' | 'unhandledrejection';
}

const KEY = 'assetlist.crashLog';
const MAX = 10;

export function readCrashLog(): CrashEntry[] {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as CrashEntry[]) : [];
  } catch {
    return [];
  }
}

export function recordCrash(kind: CrashEntry['kind'], error: unknown): CrashEntry {
  const e = error as { message?: unknown; stack?: unknown } | null;
  const entry: CrashEntry = {
    at: Date.now(),
    url: typeof location !== 'undefined' ? location.pathname + location.search : '',
    message: e && typeof e === 'object' && typeof e.message === 'string' ? e.message : String(error),
    stack: e && typeof e === 'object' && typeof e.stack === 'string' ? e.stack.slice(0, 4000) : undefined,
    kind,
  };
  try {
    localStorage.setItem(KEY, JSON.stringify([entry, ...readCrashLog()].slice(0, MAX)));
  } catch {
    // storage unavailable: the entry is still returned for display
  }
  return entry;
}

export function clearCrashLog() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}

export function formatCrash(c: CrashEntry): string {
  return `${new Date(c.at).toISOString()} ${c.kind} ${c.url}\n${c.message}${c.stack ? `\n${c.stack}` : ''}`;
}

/** Captures errors that escape React's tree (event handlers, timers, unawaited promises). Call once at startup. */
export function installGlobalCrashCapture() {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (ev) => {
    recordCrash('error', ev.error ?? ev.message);
  });
  window.addEventListener('unhandledrejection', (ev) => {
    recordCrash('unhandledrejection', ev.reason);
  });
}
